import gsap, { reduced, SplitText } from "../gsap";
import hey from "../hey";
import { Dom } from "../dom";


/**
 * Preloader - Logo Hero Intro Animation
 *
 * For this onepager, the preloader IS the logo hero section.
 * It plays the logo animation, animates the title words in, then reveals
 * the navigation and unlocks scroll. The element stays visible (it's part of the page content).
 *
 * HTML Structure:
 * <section data-preloader>
 *   <div data-preloader-logo>
 *     <svg>
 *       <path class="square">...</path>
 *       <path class="M">...</path>
 *       <path class="A">...</path>
 *       <path class="m">...</path>
 *       <circle class="o">...</circle>
 *     </svg>
 *   </div>
 *   <h1 class="hero-title">Your Title Here</h1>
 * </section>
 *
 * The title text is split into words and animated in with a stagger.
 * After animation completes, the SplitText is reverted to restore normal HTML for resizing.
 * Navigation should have [data-nav] attribute and start hidden.
 */

class _Preloader {
  #element;
  #logo;
  #title;
  #titleSplit;
  #titleOriginalText;
  #nav;
  #parts = {};
  #timeline;
  #hasRun = false;
  #isComplete = false;
  #onCompleteCallbacks = [];

  constructor() {
    this.#element = document.querySelector("[data-preloader]");
    this.#nav = document.querySelector("[data-nav]");

    if (this.#element) {
      this.#logo = this.#element.querySelector("[data-preloader-logo]") || this.#element.querySelector("svg");
      this.#title = this.#element.querySelector(".hero-title");
    }
  }

  /**
   * Check if preloader exists in DOM
   */
  get exists() {
    return !!this.#element;
  }

  /**
   * Check if preloader has already run this session
   */
  get hasRun() {
    return this.#hasRun;
  }

  /**
   * Check if preloader animation is complete
   */
  get isComplete() {
    return this.#isComplete;
  }

  /**
   * Initialize and run the preloader
   * Only runs once per session
   * @returns {Promise} Resolves when preloader animation is complete
   */
  async init() {
    // If no preloader element or already run, resolve immediately
    if (!this.#element || this.#hasRun) {
      this.#isComplete = true;

      // Still start DOM modules and show nav if skipping
      Dom.start();
      this.#showNav();

      return Promise.resolve();
    }

    this.#hasRun = true;
    hey.PRELOADER_START = true;

    // Set up hidden states for logo elements
    this.#setupLogoElements();

    // Set up hero title for scramble animation
    this.#setupTitle();

    // Hide navigation initially
    this.#hideNav();

    // Wait for page to be ready
    await this.#waitForReady();

    // Run the logo animation
    await this.#animateLogo();

    this.#isComplete = true;
    hey.PRELOADER_COMPLETE = true;

    // Call any registered callbacks
    this.#onCompleteCallbacks.forEach((cb) => cb());

    return Promise.resolve();
  }

  /**
   * Set up logo elements in their initial hidden state
   */
  #setupLogoElements() {
    if (!this.#logo || reduced) return;

    const svg = this.#logo.tagName === "svg" ? this.#logo : this.#logo.querySelector("svg");
    if (!svg) return;

    this.#parts = {
      square: svg.querySelector(".square"),
      M: svg.querySelector(".M"),
      A: svg.querySelector(".A"),
      m: svg.querySelector(".m"),
      o: svg.querySelector(".o"),
    };

    const { square, M, A, m, o } = this.#parts;

    // Square: masked from top, will reveal downward
    if (square) {
      gsap.set(square, {
        clipPath: "inset(0% 0% 100% 0%)",
      });
    }

    // M: starts below (will slide up)
    if (M) {
      gsap.set(M, {
        y: 120,
        opacity: 0,
        transformOrigin: "center center",
      });
    }

    // A: starts to the right (will slide left)
    if (A) {
      gsap.set(A, {
        x: 150,
        opacity: 0,
        transformOrigin: "center center",
      });
    }

    // m: starts at bottom-right, rotated upright
    if (m) {
      gsap.set(m, {
        x: 380,
        rotation: -90,
        opacity: 0,
        transformOrigin: "center center",
      });
    }

    // o: will be drawn with stroke animation
    if (o) {
      gsap.set(o, {
        fill: "transparent",
        stroke: "#FF8967",
        strokeWidth: 5,
        strokeDasharray: 100,
        strokeDashoffset: 100,
        transformOrigin: "center center",
      });
    }

    // Make logo visible now that hidden states are set
    if (this.#logo.hasAttribute("data-start")) {
      this.#logo.style.visibility = "visible";
    }
    if (svg.hasAttribute("data-start")) {
      svg.style.visibility = "visible";
    }
  }

  /**
   * Set up the hero title with simple word animation
   * - Split existing HTML title into words
   * - Animate words in with stagger
   */
  #setupTitle() {
    if (!this.#title || reduced) return;

    // Store the original text
    this.#titleOriginalText = this.#title.textContent.trim();

    // Split the title into words
    this.#titleSplit = SplitText.create(this.#title, {
      type: "words",
      mask: "words",
      wordsClass: "word",
    });

    // Hide words below their mask (they'll animate in)
    gsap.set(this.#titleSplit.words, {
      yPercent: 110,
    });

    // Make title visible now that hidden state is set
    this.#title.style.visibility = "visible";
  }

  /**
   * Hide navigation initially
   */
  #hideNav() {
    if (!this.#nav) return;
    gsap.set(this.#nav, {
      opacity: 0,
      visibility: "hidden",
    });
  }

  /**
   * Show navigation with fade animation
   */
  #showNav() {
    if (!this.#nav) return;
    gsap.to(this.#nav, {
      opacity: 1,
      visibility: "visible",
      duration: 0.6,
      ease: "power2.out",
    });
  }

  /**
   * Wait for the page to be ready
   */
  async #waitForReady() {
    // Wait for DOM content loaded
    if (document.readyState !== "complete") {
      await new Promise((resolve) => {
        window.addEventListener("load", resolve, { once: true });
      });
    }

    // Small delay to ensure everything is rendered
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  /**
   * Animate the logo elements
   */
  async #animateLogo() {
    return new Promise((resolve) => {
      // If reduced motion or no parts, skip animation
      if (reduced || !Object.values(this.#parts).some(Boolean)) {
        Dom.start();
        this.#showNav();
        resolve();
        return;
      }

      const { square, M, A, m, o } = this.#parts;

      this.#timeline = gsap.timeline({
        delay: 0.3,
        onComplete: () => {
          // Start other DOM modules
          Dom.start();
          // Show navigation
          this.#showNav();
          resolve();
        },
      });

      // ═══════════════════════════════════════════════════════════════
      // SQUARE - Grows from top with clip-path mask
      // ═══════════════════════════════════════════════════════════════
      if (square) {
        this.#timeline.to(square, {
          clipPath: "inset(0% 0% 0% 0%)",
          duration: 0.6,
          ease: "power3.inOut",
        });
      }

      // ═══════════════════════════════════════════════════════════════
      // M - Comes in from bottom, slides up with bounce
      // ═══════════════════════════════════════════════════════════════
      if (M) {
        this.#timeline.to(
          M,
          {
            opacity: 1,
            duration: 0.2,
            ease: "power2.out",
          },
          "-=0.2"
        );

        this.#timeline.to(
          M,
          {
            y: 0,
            duration: 0.5,
            ease: "back.out(0.9)",
          },
          "-=0.15"
        );
      }

      // ═══════════════════════════════════════════════════════════════
      // A - Comes in from right, slides left with bounce
      // ═══════════════════════════════════════════════════════════════
      if (A) {
        this.#timeline.to(
          A,
          {
            opacity: 1,
            duration: 0.2,
            ease: "power2.out",
          },
          "-=0.35"
        );

        this.#timeline.to(
          A,
          {
            x: 0,
            duration: 0.5,
            ease: "back.out(1.4)",
          },
          "-=0.15"
        );
      }


      // ═══════════════════════════════════════════════════════════════
      // m - Comes from bottom-right, stops with bounce, rotates, slides left
      // ═══════════════════════════════════════════════════════════════
      if (m) {
        // Fade in
        this.#timeline.to(
          m,
          {
            opacity: 1,
            duration: 0.15,
            ease: "power2.out",
          },
          "-=0.5"
        );

        // Phase 1: Slide in to bottom-right staging position with tiny bounce
        this.#timeline.to(
          m,
          {
            x: 250,
            y: 0,
            duration: 0.4,
            ease: "back.out(1.2)",
          },
          "-=0.1"
        );

        // Phase 2: Rotate (rotation only)
        this.#timeline.to(m, {
          rotation: 0,
          duration: 0.35,
          ease: "power3.out",
        });

        // Phase 3: Slide left to final position with tiny bounce
        this.#timeline.to(m, {
          x: 0,
          duration: 0.45,
          ease: "back.out(1.2)",
        });
      }


      // ═══════════════════════════════════════════════════════════════
      // o - Circle drawn clockwise
      // ═══════════════════════════════════════════════════════════════
      if (o) {
        this.#timeline.to(
          o,
          {
            strokeDashoffset: 0,
            strokeWidth: 5,
            duration: 1.5,
            ease: "cubic-bezier(.09,.38,.04,.94)",
          },
          "-=0.4"
        );
      }

      // ═══════════════════════════════════════════════════════════════
      // HERO TITLE - Simple word animation
      // Words slide up into view with stagger
      // ═══════════════════════════════════════════════════════════════
    //   if (this.#titleSplit && this.#titleSplit.words) {
    //     this.#timeline.to(
    //       this.#titleSplit.words,
    //       {
    //         yPercent: 0,
    //         duration: 0.6,
    //         ease: "power3.out",
    //         stagger: 0.08,
    //         onComplete: () => {
    //           // Revert SplitText after animation to restore normal HTML
    //           // This helps with resizing behavior
    //           if (this.#titleSplit) {
    //             this.#titleSplit.revert();
    //             this.#titleSplit = null;
    //           }
    //         },
    //       },
    //       "-=0.3" // Overlap with "m" slide
    //     );
    //   }
    });
  }

  /**
   * Register a callback for when preloader completes
   * Useful for modules that need to know when to start
   */
  onComplete(callback) {
    if (this.#isComplete) {
      callback();
    } else {
      this.#onCompleteCallbacks.push(callback);
    }
  }

  /**
   * Skip the preloader (for development)
   */
  skip() {
    // Kill any running animation
    if (this.#timeline) {
      this.#timeline.progress(1);
      this.#timeline.kill();
    }

    // Revert title split if it exists
    if (this.#titleSplit) {
      this.#titleSplit.revert();
      this.#titleSplit = null;
    }

    // Ensure title is visible
    if (this.#title) {
      this.#title.style.visibility = "visible";
    }

    this.#hasRun = true;
    this.#isComplete = true;
    hey.PRELOADER_COMPLETE = true;

    // Show nav and start modules
    this.#showNav();
    Dom.start();
  }
}

export const Preloader = new _Preloader();
